import React from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';
import { bubEvents } from 'javascripts/shared/events/bub_events';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { CoverImage } from 'javascripts/components/cover_image';
import { Modal } from 'javascripts/components/modal';
import { StarRating } from 'javascripts/components/star_rating';
import { AuthorByline } from 'javascripts/components/book_card/author_byline';
import { AddToCartButton } from 'javascripts/components/add_to_cart_button';

import styles from 'stylesheets/components/checkout/book_details_modal';

export function BookDetailsModal({ audiobook, bundle, isOpen, onRequestClose, showPurchaseInfo, showBuyNowAndAddToCartCtas, addToCartOnClickHandler }) {
  function handleCoverClick() {
    bubEvents.track('Book details modal', 'Clicked cover', {
      label: 'More details',
      value: audiobook.id
    });
  }

  function handleTitleClick() {
    bubEvents.track('Book details modal', 'Clicked title', {
      label: 'More details',
      value: audiobook.id
    });
  }

  function handleMoreDetailsClick() {
    bubEvents.track('Book details modal', 'Clicked button', {
      label: 'More details',
      value: audiobook.id
    });
  }

  function handleCloseClick() {
    bubEvents.track('Book details modal', 'Clicked button', {
      label: 'Close',
      value: audiobook.id
    });
    onRequestClose();
  }

  function handleBuyNowClick() {
    bubEvents.track('Book details modal', 'Clicked buy now button', {
      label: 'Audiobook id',
      value: audiobook.id
    });

    if (window.gtag !== undefined) {
      gtag('event', 'buy_now', {
        event_category: 'purchase',
        event_label: audiobook.displayTitle,
      });
    }
  }

  function handleBuyBundleClick() {
    bubEvents.track('Book details modal', 'Clicked buy bundle button', {
      label: 'Bundle id',
      value: bundle.id
    });
  }

  function renderStarRating() {
    if (audiobook.ratingsCount < 5 || !_.isFinite(audiobook.averageRating)) {
      return null;
    }

    return (
      <div className={styles.averageRatingWrapper}>
        <StarRating
          rating={audiobook.averageRating}
          ratingsCount={audiobook.ratingsCount}
          ratingsCountLink={`${audiobook.url}?reviews=true`}
          showRating
          showRatingsCount
          size='large'
          textSize='medium'
        />
      </div>
    );
  }

  function renderDescription(htmlText, isShortened = false) {
    return (
      <div
        className={isShortened ? styles.descriptionTextShortened : styles.descriptionText }
        data-testid='audiobook-description-section-description'
        dangerouslySetInnerHTML={{ __html: htmlText }}
      ></div>
    );
  }

  function renderShortenedDescription(htmlText) {
    return (
      <div className={styles.showMoreDescription}>
        {renderDescription(htmlText, true)}
        <a className={styles.showMoreLink} href={audiobook.url} onClick={handleMoreDetailsClick}>more</a>
      </div>
    );
  }

  function renderPrimaryDescription() {
    if (showPurchaseInfo) {
      return renderShortenedDescription(audiobook.editorialDescription || audiobook.bookDescriptionShort);
    }
  
    if (!_.isEmpty(audiobook.editorialDescription)) {
      return renderDescription(audiobook.editorialDescription);
    }

    if (_.isEmpty(audiobook.bookDescriptionShort)) {
      return renderDescription(audiobook.bookDescription);
    }

    return renderShortenedDescription(audiobook.bookDescriptionShort);
  }

  function renderBundleBuyButton() {
    if (_.isEmpty(bundle)) { return null; }

    return (
      <ChirpButton
        class={styles.ctaButton}
        displayType='button'
        href={bundle.clickthroughPath}
        label={`Buy the bundle for ${bundle.bundlePriceText}`}
        onClick={handleBuyBundleClick}
        fullWidth
      />
    );
  }

  function renderCtas() {
    if (showPurchaseInfo) {
      return (
        <>
          <ChirpButton
            class={styles.ctaButton}
            displayStyle='yellow'
            displayType='button'
            href={audiobook.currentProduct?.purchaseUrl}
            label={`Buy this book for ${audiobook.currentProduct.discountPrice}`}
            onClick={handleBuyNowClick}
            fullWidth
          />
          {renderBundleBuyButton()}
        </>
      ); 
    }

    if (showBuyNowAndAddToCartCtas) {
      return (
        <>
          <ChirpButton
            class={styles.ctaButton}
            displayStyle='yellow'
            displayType='button'
            href={audiobook.currentProduct?.purchaseUrl}
            label='Buy Now'
            onClick={handleBuyNowClick}
            fullWidth
          />
          <div className={styles.ctaButton}>
            <AddToCartButton
              audiobook={audiobook}
              inCart={!_.isNil(audiobook.currentUserShoppingCartItem)}
              inWishlist={!_.isNil(audiobook.currentUserWishlistItem)}
              trackingCategory='Book details modal'
              onClick={addToCartOnClickHandler}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <ChirpButton
          class={styles.ctaButton}
          displayType='button'
          href={audiobook.url}
          label='More Details'
          onClick={handleMoreDetailsClick}
          fullWidth
        />
      </>
    );
  }

  function renderRuntime() {
    if (!audiobook.runtime) { return null; }

    return (
      <div className={styles.runtime}>
        <span className={styles.runtimeLabel}>Runtime: </span>
        {audiobook.runtime}
      </div>
    );
  }

  function renderModalContent() {
    return (
      <div className={styles.bookDetailsModal} data-testid='book-details-modal'>
        <div className={styles.bookWrapper}>
          <div className={styles.coverImageContainer}>
            <CoverImage
              bookAuthor={audiobook.displayAuthors}
              bookTitle={audiobook.displayTitle}
              coverUrl={audiobook.coverUrl}
              linkUrl={audiobook.url}
              onClick={handleCoverClick}
              defer={false}
            />
          </div>
          <div className={styles.bookInfo}>
            <div className={styles.bookTitle}><a href={audiobook.url} onClick={handleTitleClick}>{audiobook.displayTitle}</a></div>
            <div className={styles.authorInfo}>
              <AuthorByline audiobook={audiobook} trackingCategory="Book details modal" />
            </div>
            {renderStarRating()}
            {renderRuntime()}
          </div>
        </div>
        <div className={styles.descriptionSection}>
          <div className={styles.descriptionHeading}>Description</div>
          {renderPrimaryDescription()}
        </div>
        {renderCtas()}
        <ChirpButton
          displayType='link'
          label='Close'
          onClick={handleCloseClick}
          fullWidth
        />
      </div>
    );
  }

  return (
    <Modal appSelector='#add-to-cart-button' isOpen={isOpen} onRequestClose={handleCloseClick}>
      {renderModalContent()}
    </Modal>
  );
}

BookDetailsModal.propTypes = {
  audiobook: ChirpPropShapes.audiobook.isRequired,
  bundle: PropTypes.shape({
    bundlePriceText: PropTypes.string,
    clickthroughPath: PropTypes.string,
    id: PropTypes.string
  }),
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func,
  showPurchaseInfo: PropTypes.bool.isRequired,
  showBuyNowAndAddToCartCtas: PropTypes.bool.isRequired,
  addToCartOnClickHandler: PropTypes.func.isRequired,
};

BookDetailsModal.defaultProps = {
  bundle: {},
  isOpen: false,
  showPurchaseInfo: false,
  showBuyNowAndAddToCartCtas: false,
  addToCartOnClickHandler: _.noop,
};
